import React from 'react';
import { Item } from './upsertItem';
import { CatalogProxy } from '../../services/catalogServiceAPI/catalogProxy';
import { Uploading } from '../../uploading/uploading';
import { inject, observer, Provider } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { CurrentCatalogItemStore } from "./store/currentItem";
import { validateVariantList } from "./upsertVariants/validateVariant";
import { isEmpty } from "../../utilis/validation";
import { withRouter } from 'react-router-dom';
import { doesContainFiles } from "../../services/utils";
import { VariationTypesSection } from "./upsertVariationType";
import { VariantsMatrixSection } from "./upsertVariants";
import { Button, Tabs, Alert, Typography, Space } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import './upsertItem.css';

@inject('currentUserStore')
@observer
class UpsertCatalogItemComponent extends React.Component {
    @observable loading = true;
    @observable saving = false;
    @observable errorMsg;

    catalogProxy = new CatalogProxy();

    @action.bound setLoading = (isLoading) => this.loading = isLoading;
    @action.bound setSaving = (isSaving) => this.saving = isSaving;
    @action.bound setMessage = (msg) => {
        this.errorMsg = msg instanceof Error ? msg.message : msg;
    };

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        const {id} = this.props.match.params;
        if (typeof id === 'undefined') {
            this.currentItem = new CurrentCatalogItemStore(this.props.currentUserStore);
            this.setLoading(false);
        } else {
            this.catalogProxy.get(id)
                .then((item) => this.currentItem = new CurrentCatalogItemStore(this.props.currentUserStore, item))
                .then(() => this.setLoading(false))
                .catch(() => alert(`can't load item - probably something wrong with the catalog service`));
        }
    }

    validate = (item) => {
        if (!item) {
            return Promise.reject('no current item');
        }

        if (isEmpty(item.name)) {
            return Promise.reject('Name can not be empty');
        }

        if (item.categoriesIds.length < 1) {
            return Promise.reject('At least one category is needed');
        }

        if (this.currentItem.variants.some(variant => variant.touchedExternalData.length > 0)) {
            return Promise.reject('Not all initial values have been initialized');
        }

        const errorMsg = validateVariantList(item.variants, item.type);
        if (errorMsg !== null) {
            return Promise.reject(errorMsg);
        }

        // An unlockable variation with no price would ship as a free unlock
        // (and the server rejects it); say which one before the request goes out.
        const unpriced = (item.variationsTypes || [])
            .flatMap(type => type.variations || [])
            .find(v => v.accessTier === 'unlockable' && !(Number(v.coinPrice) >= 1));
        if (unpriced) {
            return Promise.reject(`Unlockable variation "${unpriced.title || ''}" needs a coin price of at least 1`);
        }

        return Promise.resolve();
    }

    save = async () => {
        this.setSaving(true);

        try {
            const item = this.currentItem.toJSON();
            await this.validate(item);
            const doesExists = typeof item.creationDate !== 'undefined';

            delete item.creationDate;
            delete item.updatedOn;

            let id = item.id;
            if (doesExists) {
                await this.catalogProxy.update(item);
            } else {
                if (!doesContainFiles(this.currentItem.type)) {
                    item.isReady = true;
                }

                id = await this.catalogProxy.create(item);
            }

            this.setSaving(false);
            const next = doesContainFiles(this.currentItem.type) ? `/assets/${id}` : `/catalog`;
            this.props.history.push(next);
        } catch (msg) {
            this.setSaving(false);
            this.setMessage(msg);
            console.error(`can't save - `, msg);
        }
    };

    render() {
        const tabItems = [
            { key: 'data', label: 'Data', children: <Item /> },
            { key: 'variations', label: 'Variations Table', children: <VariationTypesSection /> },
            { key: 'variants', label: 'Variants', children: <VariantsMatrixSection /> },
            {
                key: 'save',
                label: 'Save Metadata',
                children: (
                    <Space direction="vertical" size={16} style={{ display: 'flex', alignItems: 'center', paddingTop: 32 }}>
                        <Typography.Text type="secondary">
                            Review the Data, Variations Table, and Variants tabs before saving.
                        </Typography.Text>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="large"
                            onClick={this.save}
                        >
                            Save Metadata
                        </Button>
                        {this.errorMsg && (
                            <Alert
                                type="error"
                                message={this.errorMsg}
                                style={{ maxWidth: 480 }}
                            />
                        )}
                    </Space>
                ),
            },
        ];

        return (
            <div className="catalog-page">
                {this.loading ?
                    'loading...' :
                    (
                        <Provider currentItem={this.currentItem}>
                            <div className="catalog-item-wrapper">
                                <Tabs items={tabItems} defaultActiveKey="data" />
                            </div>
                        </Provider>)
                }
                {this.saving && <Uploading/>}
            </div>
        );
    }
}

export const UpsertCatalogItem = withRouter(UpsertCatalogItemComponent);
